import React, { createContext, useCallback, useContext, useMemo, useState, useEffect, useRef } from 'react';
import { normalizeRole } from '../utils/roles';
import { apiUrl } from '../utils/apiUrl';
import { clearStoredAuth, getStoredToken } from '../utils/authStorage';

const AUTH_STORAGE_KEYS = new Set(['token', 'login']);

const AuthContext = createContext({
  token: null,
  user: null,
  realUser: null,
  ready: false,
  impersonatedRole: null,
  impersonateRole: () => {},
  resetImpersonation: () => {},
  logout: () => {},
  refresh: () => Promise.resolve(),
});

function decodeJwtPayload(token) {
  try {
    const payloadPart = String(token || '').split('.')[1];
    if (!payloadPart) return null;
    const normalized = payloadPart.replace(/-/g, '+').replace(/_/g, '/');
    const padded = normalized.padEnd(normalized.length + ((4 - (normalized.length % 4)) % 4), '=');
    return JSON.parse(window.atob(padded));
  } catch {
    return null;
  }
}

function buildFallbackUserFromToken(token) {
  const payload = decodeJwtPayload(token);
  if (!payload) return null;
  return {
    id: payload.id,
    email: payload.email,
    role: normalizeRole(payload.role),
    farmId: payload.farmId || null,
    farmName: null,
    farms: undefined,
    telegramLinked: false,
    telegramBotUrl: undefined,
  };
}

async function fetchCurrentUser(storedToken, cacheBust = false) {
  const suffix = cacheBust ? `?authRefresh=${Date.now()}` : '';
  return fetch(apiUrl(`/api/auth/me${suffix}`), {
    cache: 'no-store',
    headers: {
      Authorization: `Bearer ${storedToken}`,
      'Cache-Control': 'no-cache',
      Pragma: 'no-cache',
    },
  });
}

async function readAuthFailureCode(res) {
  try {
    const payload = await res.clone().json();
    return payload?.code || null;
  } catch {
    return null;
  }
}

function isDefinitiveAuthDenial(res) {
  return res.status === 401 || res.status === 403;
}

function shouldRefreshFromStorageEvent(event) {
  if (!event || !('key' in event)) return false;
  return event.key === null || AUTH_STORAGE_KEYS.has(event.key);
}

export function AuthProvider({ children }) {
  const [token, setToken] = useState(null);
  const [realUser, setRealUser] = useState(null);
  const [impersonatedRole, setImpersonatedRole] = useState(null);
  const [ready, setReady] = useState(false);
  const inFlightRefreshRef = useRef(null);
  const refreshVersionRef = useRef(0);
  const tokenRef = useRef(null);
  const readyRef = useRef(false);

  const applyAuthState = useCallback((version, updater) => {
    if (refreshVersionRef.current !== version) return false;
    updater();
    return true;
  }, []);

  const refresh = useCallback(() => {
    const storedToken = getStoredToken();
    const inFlightRefresh = inFlightRefreshRef.current;
    if (inFlightRefresh && inFlightRefresh.token === storedToken) {
      return inFlightRefresh.promise;
    }

    const version = ++refreshVersionRef.current;
    const promise = (async () => {
      if (!storedToken) {
        clearStoredAuth();
        applyAuthState(version, () => {
          tokenRef.current = null;
          readyRef.current = true;
          setToken(null);
          setRealUser(null);
          setImpersonatedRole(null);
          setReady(true);
        });
        return null;
      }

      try {
        let res = await fetchCurrentUser(storedToken);
        if (res.status === 304) {
          res = await fetchCurrentUser(storedToken, true);
        }
        if (res.status === 429) {
          applyAuthState(version, () => {
            tokenRef.current = storedToken;
            readyRef.current = true;
            setToken(storedToken);
            setRealUser(
              (current) => current || buildFallbackUserFromToken(storedToken)
            );
            setImpersonatedRole(null);
            setReady(true);
          });
          return null;
        }
        if (isDefinitiveAuthDenial(res)) {
          const code = await readAuthFailureCode(res);
          console.warn('auth_refresh_denied', { status: res.status, code });
          clearStoredAuth();
          applyAuthState(version, () => {
            tokenRef.current = null;
            readyRef.current = true;
            setToken(null);
            setRealUser(null);
            setImpersonatedRole(null);
            setReady(true);
          });
          return null;
        }
        if (!res.ok) {
          const code = await readAuthFailureCode(res);
          console.warn('auth_refresh_unavailable', {
            status: res.status,
            code
          });
          const fallbackUser = buildFallbackUserFromToken(storedToken);
          applyAuthState(version, () => {
            tokenRef.current = storedToken;
            readyRef.current = true;
            setToken(storedToken);
            setRealUser((current) => current || fallbackUser);
            setImpersonatedRole(null);
            setReady(true);
          });
          return fallbackUser;
        }
        const data = await res.json();
        const nextUser = {
          id: data.id,
          email: data.email,
          role: normalizeRole(data.role),
          farmId: data.farmId,
          farmName: data.farmName,
          farms: data.farms,
          telegramLinked: Boolean(data.telegramLinked),
          telegramBotUrl: data.telegramBotUrl,
        };
        applyAuthState(version, () => {
          tokenRef.current = storedToken;
          setToken(storedToken);
          setRealUser(nextUser);
          setImpersonatedRole(null);
        });
        return nextUser;
      } catch (err) {
        console.warn('auth_refresh_network_error', {
          name: err?.name || 'Error'
        });
        const fallbackUser = buildFallbackUserFromToken(storedToken);
        applyAuthState(version, () => {
          tokenRef.current = storedToken;
          setToken(storedToken);
          setRealUser((current) => current || fallbackUser);
          setImpersonatedRole(null);
        });
        return fallbackUser;
      } finally {
        applyAuthState(version, () => {
          readyRef.current = true;
          setReady(true);
        });
      }
    })();

    inFlightRefreshRef.current = { token: storedToken, promise };
    promise.finally(() => {
      if (inFlightRefreshRef.current?.promise === promise) {
        inFlightRefreshRef.current = null;
      }
    });
    return promise;
  }, [applyAuthState]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  useEffect(() => {
    const handleStorage = (event) => {
      if (!shouldRefreshFromStorageEvent(event)) return;
      const storedToken = getStoredToken();
      if (readyRef.current && storedToken === tokenRef.current) return;
      refresh();
    };
    window.addEventListener('storage', handleStorage);
    return () => window.removeEventListener('storage', handleStorage);
  }, [refresh]);

  const logout = useCallback(() => {
    refreshVersionRef.current += 1;
    inFlightRefreshRef.current = null;
    clearStoredAuth();
    tokenRef.current = null;
    readyRef.current = true;
    setToken(null);
    setRealUser(null);
    setImpersonatedRole(null);
    setReady(true);
    window.dispatchEvent(new Event('storage'));
  }, []);

  const impersonateRole = useCallback((role) => {
    if (normalizeRole(realUser?.role) !== 'admin') return;
    const nextRole = normalizeRole(role);
    setImpersonatedRole(nextRole);
  }, [realUser?.role]);

  const resetImpersonation = useCallback(() => {
    setImpersonatedRole(null);
  }, []);

  const user = useMemo(() => {
    if (!realUser) return null;
    if (!impersonatedRole) return realUser;
    return { ...realUser, role: impersonatedRole };
  }, [realUser, impersonatedRole]);

  const value = useMemo(
    () => ({
      token,
      user,
      realUser,
      ready,
      impersonatedRole,
      impersonateRole,
      resetImpersonation,
      logout,
      refresh,
    }),
    [token, user, realUser, ready, impersonatedRole, impersonateRole, resetImpersonation, logout, refresh]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  return useContext(AuthContext);
}
